// src/features/monetize/components/ProductsTab.tsx

import { useState } from 'react';
import { ActionIcon, Badge, Button, Group, Menu, Paper, Pill, Stack, Table, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconArchive, IconDotsVertical, IconPencil, IconPlayerPlay, IconPlus, IconTag } from '@tabler/icons-react';
import { PRODUCT_STATUS_COLORS, PRODUCT_STATUS_LABELS, PRODUCT_TYPE_LABELS } from '@/lib/constants/monetize';
import type { GatingSync, MonetizeProduct, ProductStatus } from '@/lib/types/Monetize';
import { MonetizeUtils } from '../MonetizeUtils';
import { useSetProductStatus } from '../useMonetizeMutations';
import { AddPriceModal } from './AddPriceModal';
import { ProductDrawer } from './ProductDrawer';
import { useScopeLookups } from './ScopePicker';

interface ProductsTabProps {
  products: MonetizeProduct[];
  onGatingSync: (sync: GatingSync | undefined) => void;
}

export function ProductsTab({ products, onGatingSync }: ProductsTabProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<MonetizeProduct | null>(null);
  const [pricing, setPricing] = useState<MonetizeProduct | null>(null);
  const setStatus = useSetProductStatus();
  const { lookups } = useScopeLookups();

  function open(product: MonetizeProduct | null) {
    setEditing(product);
    setDrawerOpen(true);
  }

  async function changeStatus(product: MonetizeProduct, status: ProductStatus) {
    try {
      const res = await setStatus.mutateAsync({ productId: product.id, status });
      notifications.show({ title: PRODUCT_STATUS_LABELS[status], message: `"${product.name}" is now ${PRODUCT_STATUS_LABELS[status].toLowerCase()}.`, color: 'green' });
      onGatingSync(res.gatingSync);
    } catch (err) {
      notifications.show({ title: 'Error', message: MonetizeUtils.errorMessage(err, 'Failed to change the status.'), color: 'red' });
    }
  }

  function confirmArchive(product: MonetizeProduct) {
    modals.openConfirmModal({
      title: `Archive "${product.name}"?`,
      children: (
        <Text size="sm">
          Its content opens up again unless another active product covers it. Viewers who already bought it keep their access.
        </Text>
      ),
      labels: { confirm: 'Archive', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => changeStatus(product, 'archived'),
    });
  }

  const active = products.filter((p) => p.status !== 'archived');
  const archived = products.filter((p) => p.status === 'archived');

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          A product is what a viewer buys: a subscription, an event or season pass, or a rental. Active products put their content behind a purchase.
        </Text>
        <Button leftSection={<IconPlus size={16} />} onClick={() => open(null)}>New product</Button>
      </Group>

      {products.length === 0 ? (
        <Paper withBorder p="xl">
          <Stack align="center" gap="xs">
            <Text fw={500}>No products yet</Text>
            <Text size="sm" c="dimmed" ta="center">Start from a template, or create a product and pick what it includes.</Text>
            <Button variant="light" leftSection={<IconPlus size={16} />} onClick={() => open(null)}>New product</Button>
          </Stack>
        </Paper>
      ) : (
        <Paper withBorder style={{ overflow: 'clip' }}>
          <Table highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Product</Table.Th>
                <Table.Th>Includes</Table.Th>
                <Table.Th>Prices</Table.Th>
                <Table.Th>Window</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th w={48} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {[...active, ...archived].map((p) => (
                <Table.Tr key={p.id} style={p.status === 'archived' ? { opacity: 0.6 } : undefined}>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <Text fw={500} size="sm">{p.name}</Text>
                      <Badge size="xs" variant="outline" color="gray">{PRODUCT_TYPE_LABELS[p.type]}</Badge>
                    </Group>
                    {p.description && <Text size="xs" c="dimmed" lineClamp={1}>{p.description}</Text>}
                  </Table.Td>
                  <Table.Td>
                    <Pill.Group>
                      {p.scopes.slice(0, 3).map((s) => <Pill key={s} size="sm">{MonetizeUtils.scopeLabel(s, lookups)}</Pill>)}
                      {p.scopes.length > 3 && <Pill size="sm">+{p.scopes.length - 3} more</Pill>}
                    </Pill.Group>
                  </Table.Td>
                  <Table.Td><Text size="sm">{MonetizeUtils.priceSummary(p)}</Text></Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {p.type === 'pass' && (p.accessEndsAt ? `Through ${MonetizeUtils.formatWhen(p.accessEndsAt)}` : 'No end')}
                      {p.type === 'rental' && `${Math.round((p.rentalWindowSeconds ?? 0) / 3600)} h after purchase`}
                      {p.type === 'subscription' && 'While subscribed'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={PRODUCT_STATUS_COLORS[p.status]} variant="light" size="sm">{PRODUCT_STATUS_LABELS[p.status]}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Menu position="bottom-end" withinPortal>
                      <Menu.Target>
                        <ActionIcon variant="subtle" color="gray" aria-label="Product actions"><IconDotsVertical size={16} /></ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => open(p)}>Edit</Menu.Item>
                        {p.status !== 'archived' && <Menu.Item leftSection={<IconTag size={14} />} onClick={() => setPricing(p)}>Add a price</Menu.Item>}
                        {p.status !== 'active' && <Menu.Item leftSection={<IconPlayerPlay size={14} />} onClick={() => changeStatus(p, 'active')}>Activate</Menu.Item>}
                        {p.status === 'active' && <Menu.Item onClick={() => changeStatus(p, 'draft')}>Set as draft</Menu.Item>}
                        {p.status !== 'archived' && (
                          <Menu.Item color="red" leftSection={<IconArchive size={14} />} onClick={() => confirmArchive(p)}>Archive</Menu.Item>
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <ProductDrawer
        opened={drawerOpen}
        product={editing}
        onClose={() => setDrawerOpen(false)}
        onSaved={(sync) => { setDrawerOpen(false); onGatingSync(sync); }}
        onAddPrice={(p) => setPricing(p)}
      />
      <AddPriceModal product={pricing} onClose={() => setPricing(null)} />
    </Stack>
  );
}
